/**
 * Corporate layout web components — header, language pill, hero carousel, brief form.
 */

class CorporateScrollAnchor {
  static get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  static getHashFromLink(link) {
    const href = link.getAttribute('href') || '';
    if (!href) return null;
    if (href.charAt(0) === '#') return href;

    try {
      const url = new URL(href, window.location.origin);
      if (url.pathname === window.location.pathname && url.hash) {
        return url.hash;
      }
    } catch {
      return null;
    }

    return null;
  }

  static scrollToElement(target, options = {}) {
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element) return false;

    element.scrollIntoView({
      behavior: CorporateScrollAnchor.prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });

    if (options.updateHistory !== false && element.id) {
      history.pushState(null, '', `#${element.id}`);
    }

    return true;
  }

  static scrollToHash(hash, options = {}) {
    if (!hash) return false;
    return CorporateScrollAnchor.scrollToElement(hash, options);
  }

  static initHashOnLoad() {
    if (!window.location.hash) return;

    window.requestAnimationFrame(() => {
      CorporateScrollAnchor.scrollToHash(window.location.hash, { updateHistory: false });
    });
  }
}

class CorporateScrollCue extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;

    this.#button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
  }

  #button = null;

  #onClick = () => {
    const selector = this.getAttribute('scroll-target');
    if (!selector) return;

    CorporateScrollAnchor.scrollToElement(selector, { updateHistory: false });
  };
}

class CorporateLanguagePill extends HTMLElement {
  connectedCallback() {
    this.#switchLink = this.querySelector('[data-corporate-lang-switch]');
    if (!this.#switchLink) return;

    this.#switchLink.addEventListener('click', this.#onSwitchClick);
  }

  disconnectedCallback() {
    this.#switchLink?.removeEventListener('click', this.#onSwitchClick);
  }

  #switchLink = null;

  #onSwitchClick = (event) => {
    const targetLang = (this.#switchLink?.getAttribute('data-corporate-lang-switch') || '').toLowerCase();
    if (!targetLang) return;

    event.preventDefault();

    let normalized = window.location.pathname.replace(/^\/(ar|en)(?=\/|$)/, '') || '/';
    if (normalized.charAt(0) !== '/') normalized = `/${normalized}`;

    const nextPath =
      targetLang === 'ar' ? (normalized === '/' ? '/ar' : `/ar${normalized}`) : normalized;

    localStorage.setItem('preferred_language', targetLang);
    sessionStorage.setItem('language_applied', 'true');
    window.location.assign(nextPath + window.location.search + window.location.hash);
  };
}

class CorporateHeader extends HTMLElement {
  connectedCallback() {
    this.#toggle = this.querySelector('[data-corporate-menu-toggle]');
    this.#drawer = this.querySelector('[data-corporate-drawer]');
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (this.#toggle && this.#drawer) {
      this.#panel = this.#drawer.querySelector('.corporate-drawer__panel');

      this.#toggle.addEventListener('click', this.#onToggleClick, { signal });
      this.#drawer.querySelectorAll('[data-corporate-drawer-close]').forEach((trigger) => {
        trigger.addEventListener('click', this.#onCloseClick, { signal });
      });

      this.querySelectorAll('.corporate-drawer__link, .corporate-header__cta--drawer').forEach((link) => {
        link.addEventListener('click', this.#onDrawerLinkClick, { signal });
      });

      document.addEventListener('keydown', this.#onDocumentKeydown, { signal });

      if (this.#panel) {
        this.#panel.addEventListener('keydown', this.#onPanelKeydown, { signal });
      }
    }

    this.querySelectorAll('.corporate-header__nav-link, .corporate-drawer__link').forEach((link) => {
      link.addEventListener('click', this.#onAnchorClick, { signal });
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #toggle = null;
  #drawer = null;
  #panel = null;
  #abortController = null;

  #setOpen(isOpen) {
    if (!this.#toggle || !this.#drawer) return;

    this.#toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    this.#drawer.classList.toggle('is-open', isOpen);
    this.#drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    document.documentElement.classList.toggle('corporate-drawer-open', isOpen);

    if (isOpen) {
      this.#drawer.querySelector('.corporate-drawer__close')?.focus();
    }
  }

  #onToggleClick = () => {
    const isOpen = this.#toggle?.getAttribute('aria-expanded') === 'true';
    this.#setOpen(!isOpen);
  };

  #onCloseClick = () => {
    this.#setOpen(false);
    this.#toggle?.focus();
  };

  #onDrawerLinkClick = (event) => {
    const link = event.currentTarget;
    if (!CorporateScrollAnchor.getHashFromLink(link)) {
      this.#setOpen(false);
    }
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.#toggle?.getAttribute('aria-expanded') === 'true') {
      this.#setOpen(false);
      this.#toggle.focus();
    }
  };

  #onPanelKeydown = (event) => {
    if (event.key !== 'Tab' || this.#toggle?.getAttribute('aria-expanded') !== 'true') return;

    const focusable = this.#panel?.querySelectorAll('a[href], button:not([disabled])');
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #onAnchorClick = (event) => {
    const link = event.currentTarget;
    const hash = CorporateScrollAnchor.getHashFromLink(link);
    if (!hash || !document.querySelector(hash)) return;

    event.preventDefault();
    this.#setOpen(false);
    CorporateScrollAnchor.scrollToHash(hash);
  };
}

class CorporateHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-corporate-hero-slide]')];
    this.#indicators = [...this.querySelectorAll('[data-corporate-hero-indicator]')];
    if (!this.#slides.length) return;

    this.#intervalMs = Number(this.dataset.autoplaySeconds || 5) * 1000;
    this.#activeIndex = this.#slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (this.#activeIndex < 0) this.#activeIndex = 0;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#indicators.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.#setActive(index);
        this.#start();
      }, { signal });
    });

    this.addEventListener('mouseenter', this.#stop, { signal });
    this.addEventListener('mouseleave', this.#start, { signal });
    this.addEventListener('focusin', this.#stop, { signal });
    this.addEventListener('focusout', this.#start, { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });

    this.#setActive(this.#activeIndex);
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #slides = [];
  #indicators = [];
  #activeIndex = 0;
  #intervalMs = 0;
  #timer = null;
  #touchStartX = 0;
  #abortController = null;

  #setActive(index) {
    this.#activeIndex = (index + this.#slides.length) % this.#slides.length;

    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.#activeIndex);
    });

    this.#indicators.forEach((button, i) => {
      button.classList.toggle('is-active', i === this.#activeIndex);
      button.setAttribute('aria-current', i === this.#activeIndex ? 'true' : 'false');
    });
  }

  #stop = () => {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #start = () => {
    this.#stop();
    if (this.#intervalMs > 0 && this.#slides.length > 1) {
      this.#timer = setInterval(() => this.#setActive(this.#activeIndex + 1), this.#intervalMs);
    }
  };

  #onTouchStart = (event) => {
    this.#touchStartX = event.changedTouches[0].screenX;
  };

  #onTouchEnd = (event) => {
    const delta = event.changedTouches[0].screenX - this.#touchStartX;
    if (Math.abs(delta) < 40) return;
    this.#setActive(this.#activeIndex + (delta < 0 ? 1 : -1));
    this.#start();
  };
}

class CorporateBriefForm extends HTMLElement {
  static STORAGE_KEY = 'corporateBriefStage';
  static BRIEF_ID_KEY = 'corporateBriefId';
  static WEBHOOK_TIMEOUT_MS = 60000;

  connectedCallback() {
    this.#strings = CorporateBriefForm.#loadStrings();
    this.#startedAt = Date.now();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#panels = {
      stage1: this.querySelector('[data-brief-panel="stage-1"]'),
      success: this.querySelector('[data-brief-panel="success"]'),
    };

    this.#stage1Form = document.getElementById('CorporateBriefForm');
    this.#scalePanel = this.querySelector('[data-brief-scale]');
    this.#intentInput = this.querySelector('[data-brief-intent-input]');
    this.#intentButtons = this.querySelectorAll('[data-brief-intent]');
    this.#timingFields = {
      exact: this.querySelector('[data-brief-timing="exact"]'),
      approximate: this.querySelector('[data-brief-timing="approximate"]'),
    };

    this.#intentButtons.forEach((button) => {
      button.addEventListener('click', () => this.#setIntent(button.dataset.briefIntent), { signal });
    });

    this.querySelectorAll('input[name="timing_type"]').forEach((input) => {
      input.addEventListener('change', () => this.#updateTimingFields(), { signal });
    });

    this.#stage1Form?.addEventListener('submit', this.#onStage1Submit, { signal });

    this.#bindReactiveValidation();
    this.#initPanels();
    this.#updateTimingFields();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  static #decodeHtmlEntities(value) {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  static #loadStrings() {
    const node = document.getElementById('CorporateBriefStrings');
    if (!node) return {};

    try {
      const parsed = JSON.parse(node.textContent);
      return Object.fromEntries(
        Object.entries(parsed).map(([key, value]) => [
          key,
          CorporateBriefForm.#decodeHtmlEntities(value),
        ])
      );
    } catch {
      return {};
    }
  }

  #strings = {};
  #panels = {};
  #stage1Form = null;
  #scalePanel = null;
  #intentInput = null;
  #intentButtons = [];
  #timingFields = {};
  #startedAt = 0;
  #abortController = null;
  #submitting = false;

  static CONFIRMATION_PARAM = 'brief';
  static CONFIRMATION_VALUE = 'received';

  #isConfirmationPage() {
    return (
      new URLSearchParams(window.location.search).get(CorporateBriefForm.CONFIRMATION_PARAM) ===
      CorporateBriefForm.CONFIRMATION_VALUE
    );
  }

  #goToConfirmationPage() {
    const url = new URL(window.location.href);
    url.searchParams.set(
      CorporateBriefForm.CONFIRMATION_PARAM,
      CorporateBriefForm.CONFIRMATION_VALUE
    );
    url.hash = '';
    // A full load is required. LinkedIn's Insight Tag reads the URL when the
    // page loads and ignores hash-only changes on this same address.
    window.location.assign(url.toString());
  }

  #showPanel(name) {
    Object.entries(this.#panels).forEach(([key, panel]) => {
      if (!panel) return;
      panel.hidden = key !== name;
    });

    const section = this.closest('.corporate-brief');
    section?.classList.toggle('is-success', name === 'success');

    if (name === 'success') {
      const heading = this.querySelector('.corporate-brief__success-heading')?.textContent?.trim();
      if (heading) document.title = heading;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  #clearSession() {
    sessionStorage.removeItem(CorporateBriefForm.STORAGE_KEY);
    sessionStorage.removeItem(CorporateBriefForm.BRIEF_ID_KEY);
  }

  #initPanels() {
    this.#clearSession();
    if (this.#isConfirmationPage()) {
      this.#showPanel('success');
      this.#bindSessionClearOnExit();
      return;
    }
    this.#showPanel('stage1');
  }

  #bindSessionClearOnExit() {
    const { signal } = this.#abortController;
    const section = this.closest('.corporate-brief');

    section?.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', () => this.#clearSession(), { signal });
    });

    window.addEventListener('pagehide', () => this.#clearSession(), { signal });
  }

  #getBriefId() {
    let id = sessionStorage.getItem(CorporateBriefForm.BRIEF_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(CorporateBriefForm.BRIEF_ID_KEY, id);
    }
    return id;
  }

  #getWebhookUrl() {
    return (this.dataset.webhookUrl || '').trim();
  }

  #getWebhookToken() {
    return (this.dataset.webhookToken || '').trim();
  }

  #clearFormError(form) {
    form?.querySelector('[data-brief-form-error]')?.setAttribute('hidden', '');
    const errorNode = form?.querySelector('[data-brief-form-error]');
    if (errorNode) errorNode.textContent = '';
  }

  #showFormError(form, message) {
    const errorNode = form?.querySelector('[data-brief-form-error]');
    if (!errorNode) return;
    errorNode.textContent = message;
    errorNode.removeAttribute('hidden');
  }

  #setSubmitting(form, submitting, statusMessage = '') {
    this.#submitting = submitting;

    form?.querySelectorAll('[data-brief-submit]').forEach((button) => {
      button.disabled = submitting;
      button.classList.toggle('is-loading', submitting);
      button.setAttribute('aria-busy', submitting ? 'true' : 'false');

      const label = button.querySelector('[data-brief-submit-label]');
      if (!label) return;

      if (!button.dataset.defaultLabel) {
        button.dataset.defaultLabel = label.textContent.trim();
      }

      if (submitting && statusMessage) {
        label.textContent = statusMessage;
      } else if (!submitting) {
        label.textContent = button.dataset.defaultLabel;
      }
    });

    const processing = form?.querySelector('[data-brief-processing]');
    if (processing) {
      if (submitting && statusMessage) {
        processing.textContent = statusMessage;
        processing.removeAttribute('hidden');
      } else {
        processing.textContent = '';
        processing.setAttribute('hidden', '');
      }
    }

    form?.setAttribute('aria-busy', submitting ? 'true' : 'false');
  }

  #formatSubmitError() {
    return this.#strings.submitError || 'We could not save your brief. Please try again.';
  }

  #postToWebhook(payload) {
    const url = this.#getWebhookUrl();
    if (!url) {
      return Promise.reject(new Error('missing webhook url'));
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'text/plain;charset=utf-8');
      xhr.timeout = CorporateBriefForm.WEBHOOK_TIMEOUT_MS;

      xhr.onload = () => {
        if (xhr.status !== 200) {
          reject(new Error(`webhook http ${xhr.status}`));
          return;
        }

        const text = xhr.responseText;
        if (!text) {
          resolve();
          return;
        }

        try {
          const result = JSON.parse(text);
          if (result?.ok === false) {
            reject(new Error(result.error || 'webhook rejected'));
            return;
          }
          resolve();
        } catch {
          reject(new Error(`webhook invalid response (${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('webhook network error'));
      xhr.ontimeout = () => reject(new Error('webhook timeout'));
      xhr.send(JSON.stringify(payload));
    });
  }

  #fieldValue(form, selector) {
    return form?.querySelector(selector)?.value?.trim() || '';
  }

  #radioValue(form, name) {
    return form?.querySelector(`input[name="${name}"]:checked`)?.value || '';
  }

  #collectStage1Payload() {
    const form = this.#stage1Form;
    const phoneField = form?.querySelector('[data-brief-phone]');
    const phoneRaw = phoneField?.value?.trim() || '';
    const phoneNormalized =
      phoneField?.dataset.normalized || CorporateBriefForm.#normalizePhone(phoneRaw);

    return {
      token: this.#getWebhookToken(),
      brief_id: this.#getBriefId(),
      locale: document.documentElement.lang || 'en',
      page_url: window.location.href,
      fax: form?.querySelector('[data-brief-honeypot]')?.value || '',
      company_name: this.#fieldValue(form, '#CorporateBriefCompany'),
      contact_name: this.#fieldValue(form, '#CorporateBriefContact'),
      job_title: this.#fieldValue(form, '#CorporateBriefJobTitle'),
      website: this.#fieldValue(form, '#CorporateBriefWebsite'),
      phone: phoneRaw,
      phone_normalized: phoneNormalized,
      email: this.#fieldValue(form, '#CorporateBriefEmail'),
      city: this.#fieldValue(form, '#CorporateBriefCity'),
      employee_count: this.#radioValue(form, 'employee_count'),
      intent: this.#intentInput?.value || '',
      attendee_range: this.#radioValue(form, 'attendee_range'),
      budget_range: this.#radioValue(form, 'budget_range'),
      timing_type: this.#radioValue(form, 'timing_type'),
      event_date: this.#fieldValue(form, '#CorporateBriefEventDate'),
      approximate_date: this.#fieldValue(form, '#CorporateBriefApproxDate'),
      venue_status: this.#radioValue(form, 'venue_status'),
      elapsed_ms: String(Date.now() - this.#startedAt),
    };
  }

  #setIntent(value) {
    if (this.#intentInput) this.#intentInput.value = value;

    this.#intentButtons.forEach((button) => {
      const active = button.dataset.briefIntent === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (value) {
      this.#clearGroupError(this.querySelector('[data-brief-intent-wrap]'));
    }

    if (this.#scalePanel) {
      this.#scalePanel.hidden = value !== 'have_event';
    }

    if (value !== 'have_event') {
      this.#scalePanel?.querySelectorAll('[data-brief-group]').forEach((group) => {
        this.#clearGroupError(group);
      });

      this.#scalePanel
        ?.querySelectorAll('[data-brief-field] input, [data-brief-field] textarea')
        .forEach((field) => {
          this.#clearFieldError(field);
        });
    }
  }

  #updateTimingFields() {
    const selected = this.querySelector('input[name="timing_type"]:checked');
    const value = selected ? selected.value : '';

    if (this.#timingFields.exact) {
      this.#timingFields.exact.hidden = value !== 'exact_date';
    }

    if (this.#timingFields.approximate) {
      this.#timingFields.approximate.hidden = value !== 'approximate_date';
    }

    const exactField = this.#timingFields.exact?.querySelector('input');
    const approxField = this.#timingFields.approximate?.querySelector('input');

    if (value !== 'exact_date' && exactField) {
      this.#clearFieldError(exactField);
    }

    if (value !== 'approximate_date' && approxField) {
      this.#clearFieldError(approxField);
    }
  }

  #bindReactiveValidation() {
    const { signal } = this.#abortController;
    const form = this.#stage1Form;
    if (!form) return;

    form.querySelectorAll('[data-brief-field] input, [data-brief-field] textarea').forEach((field) => {
      const handler = () => this.#validateField(field);
      field.addEventListener('input', handler, { signal });
      field.addEventListener('change', handler, { signal });
    });

    ['employee_count', 'attendee_range', 'budget_range', 'timing_type', 'venue_status'].forEach((name) => {
      form.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
        input.addEventListener('change', () => this.#validateRadioGroup(name), { signal });
      });
    });
  }

  #clearFieldError(field) {
    const wrap = field.closest('[data-brief-field]');
    if (!wrap) return;

    wrap.classList.remove('has-error');
    const error = wrap.querySelector('[data-brief-error]');
    if (error) error.textContent = '';
  }

  #setFieldError(field, message) {
    const wrap = field.closest('[data-brief-field]');
    if (!wrap) return false;

    wrap.classList.add('has-error');
    const error = wrap.querySelector('[data-brief-error]');
    if (error) error.textContent = message;
    return false;
  }

  #clearGroupError(group) {
    if (!group) return;

    group.classList.remove('has-error');
    const error = group.querySelector('[data-brief-group-error]');
    if (error) error.textContent = '';
  }

  #setGroupError(group, message) {
    if (!group) return false;

    group.classList.add('has-error');
    const error = group.querySelector('[data-brief-group-error]');
    if (error) error.textContent = message;
    return false;
  }

  #validateField(field) {
    const wrap = field.closest('[data-brief-field]');
    if (!wrap?.classList.contains('has-error')) return;

    if (field.closest('[data-brief-scale]')?.hidden) {
      this.#clearFieldError(field);
      return;
    }

    if (wrap.hidden) {
      this.#clearFieldError(field);
      return;
    }

    const isRequired =
      field.hasAttribute('required') ||
      field.matches('[data-brief-event-date], [data-brief-approx-date]');

    if (isRequired && !field.value.trim()) return;

    if (field.matches('[data-brief-phone]')) {
      if (!field.value.trim()) return;
      const normalized = CorporateBriefForm.#normalizePhone(field.value);
      if (!normalized) return;
      field.dataset.normalized = normalized;
    }

    if (field.matches('[data-brief-email]')) {
      if (field.value.trim() && !CorporateBriefForm.#isValidEmail(field.value.trim())) return;
    }

    if (field.matches('[data-brief-website]')) {
      if (field.value.trim() && !CorporateBriefForm.#isValidWebsite(field.value.trim())) return;
    }

    this.#clearFieldError(field);
  }

  #validateRadioGroup(name) {
    const form = this.#stage1Form;
    const group = form?.querySelector(`[data-brief-group="${name}"]`);
    if (!group?.classList.contains('has-error')) return;

    if (group.closest('[data-brief-scale]')?.hidden) return;

    if (!form?.querySelector(`input[name="${name}"]:checked`)) return;

    this.#clearGroupError(group);

    if (name === 'timing_type') {
      this.#updateTimingFields();
      const timing = form.querySelector('input[name="timing_type"]:checked')?.value;
      const exactField = form.querySelector('[data-brief-event-date]');
      const approxField = form.querySelector('[data-brief-approx-date]');

      if (timing === 'exact_date' && exactField) {
        this.#validateField(exactField);
      }

      if (timing === 'approximate_date' && approxField) {
        this.#validateField(approxField);
      }
    }
  }

  static #normalizePhone(value) {
    const digits = value.replace(/\D/g, '');
    if (/^9665\d{8}$/.test(digits)) return `+${digits}`;
    if (/^05\d{8}$/.test(digits)) return `+966${digits.slice(1)}`;
    if (/^5\d{8}$/.test(digits)) return `+966${digits}`;
    return '';
  }

  static #isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  static #isValidWebsite(value) {
    const trimmed = value.trim();
    if (!trimmed) return true;
    return /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmed);
  }

  #validateStage1() {
    let valid = true;
    const honeypot = this.#stage1Form?.querySelector('[data-brief-honeypot]');
    if (honeypot?.value.trim()) return false;

    this.#stage1Form
      ?.querySelectorAll('[data-brief-group], [data-brief-intent-wrap]')
      .forEach((group) => {
        this.#clearGroupError(group);
      });

    this.#stage1Form?.querySelectorAll('[data-brief-field] input, [data-brief-field] textarea').forEach((field) => {
      this.#clearFieldError(field);
    });

    this.#stage1Form?.querySelectorAll('[required]:not([type="radio"])').forEach((field) => {
      if (field.closest('[data-brief-scale]')?.hidden) return;
      if (field.closest('[data-brief-field]')?.hidden) return;
      if (!field.value.trim()) {
        valid = this.#setFieldError(field, this.#strings.required || 'This field is required.');
      }
    });

    if (!this.#radioValue(this.#stage1Form, 'employee_count')) {
      valid = this.#setGroupError(
        this.#stage1Form?.querySelector('[data-brief-group="employee_count"]'),
        this.#strings.required || 'This field is required.'
      );
    }

    const phoneField = this.#stage1Form?.querySelector('[data-brief-phone]');
    if (phoneField?.value.trim()) {
      const normalized = CorporateBriefForm.#normalizePhone(phoneField.value);
      if (!normalized) {
        valid = this.#setFieldError(phoneField, this.#strings.phone || 'Enter a valid Saudi mobile number.');
      } else {
        phoneField.dataset.normalized = normalized;
      }
    }

    const emailField = this.#stage1Form?.querySelector('[data-brief-email]');
    if (emailField?.value.trim() && !CorporateBriefForm.#isValidEmail(emailField.value.trim())) {
      valid = this.#setFieldError(emailField, this.#strings.email || 'Enter a valid work email address.');
    }

    const websiteField = this.#stage1Form?.querySelector('[data-brief-website]');
    if (websiteField?.value.trim() && !CorporateBriefForm.#isValidWebsite(websiteField.value.trim())) {
      valid = this.#setFieldError(websiteField, this.#strings.website || 'Enter a valid website.');
    }

    if (!this.#intentInput?.value) {
      valid = this.#setGroupError(
        this.querySelector('[data-brief-intent-wrap]'),
        this.#strings.required || 'This field is required.'
      );
    }

    if (this.#intentInput?.value === 'have_event') {
      ['attendee_range', 'budget_range', 'timing_type', 'venue_status'].forEach((name) => {
        if (!this.#stage1Form?.querySelector(`input[name="${name}"]:checked`)) {
          valid = this.#setGroupError(
            this.#stage1Form?.querySelector(`[data-brief-group="${name}"]`),
            this.#strings.required || 'This field is required.'
          );
        }
      });

      const timing = this.#stage1Form?.querySelector('input[name="timing_type"]:checked')?.value;

      if (timing === 'exact_date') {
        const dateField = this.#stage1Form?.querySelector('[data-brief-event-date]');
        if (dateField && !dateField.value) {
          valid = this.#setFieldError(dateField, this.#strings.required || 'This field is required.');
        }
      }

      if (timing === 'approximate_date') {
        const approxField = this.#stage1Form?.querySelector('[data-brief-approx-date]');
        if (approxField && !approxField.value.trim()) {
          valid = this.#setFieldError(approxField, this.#strings.required || 'This field is required.');
        }
      }
    }

    return valid;
  }

  #focusFirstError() {
    const form = this.#stage1Form;
    if (!form) return;

    const firstError = form.querySelector(
      '[data-brief-field].has-error, [data-brief-group].has-error, [data-brief-intent-wrap].has-error'
    );
    if (!firstError) return;

    let focusTarget = null;

    if (firstError.matches('[data-brief-field]')) {
      focusTarget =
        firstError.querySelector('input:not([type="hidden"]), textarea, select') || firstError;
    } else if (firstError.matches('[data-brief-intent-wrap]')) {
      focusTarget = firstError.querySelector('[data-brief-intent]');
    } else {
      focusTarget =
        firstError.querySelector('input[type="radio"]') ||
        firstError.querySelector('.corporate-brief__choice') ||
        firstError;
    }

    firstError.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  #prepareStage1Submit() {
    const phoneField = this.#stage1Form?.querySelector('[data-brief-phone]');
    if (phoneField) {
      phoneField.dataset.normalized =
        phoneField.dataset.normalized || CorporateBriefForm.#normalizePhone(phoneField.value);
    }
  }

  #onStage1Submit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    if (!this.#validateStage1()) {
      this.#focusFirstError();
      return;
    }

    this.#prepareStage1Submit();
    this.#clearFormError(this.#stage1Form);

    if (!this.#getWebhookUrl() || !this.#getWebhookToken()) {
      this.#showFormError(
        this.#stage1Form,
        this.#strings.submitError || 'We could not save your brief. Please try again.'
      );
      return;
    }

    this.#setSubmitting(
      this.#stage1Form,
      true,
      this.#strings.processing || 'Saving your brief…'
    );

    let confirmed = false;

    try {
      await this.#postToWebhook(this.#collectStage1Payload());
      this.#clearSession();
      confirmed = true;
      this.#goToConfirmationPage();
    } catch {
      this.#showFormError(this.#stage1Form, this.#formatSubmitError());
    } finally {
      if (!confirmed) this.#setSubmitting(this.#stage1Form, false);
    }
  };
}

if (!customElements.get('corporate-scroll-cue')) {
  customElements.define('corporate-scroll-cue', CorporateScrollCue);
}

if (!customElements.get('corporate-language-pill')) {
  customElements.define('corporate-language-pill', CorporateLanguagePill);
}

if (!customElements.get('corporate-header')) {
  customElements.define('corporate-header', CorporateHeader);
}

if (!customElements.get('corporate-hero-carousel')) {
  customElements.define('corporate-hero-carousel', CorporateHeroCarousel);
}

if (!customElements.get('corporate-brief-form')) {
  customElements.define('corporate-brief-form', CorporateBriefForm);
}

if (document.body.classList.contains('corporate-layout')) {
  CorporateScrollAnchor.initHashOnLoad();
}
